/**
 * Composes the batched declaration hooks into the derived three-state flow view
 * every surface of the resolution flow renders. It fetches the read-model +
 * statements + article-states + condition-fields, assembles the pure StateContext
 * (flowState.ts), and derives the cell/scope/statement states + the worklist from
 * it. The derivation is the ONE place the three states come from — the screens
 * only render.
 *
 * Two layers sit ON TOP of the derivation rather than inside it:
 *
 *   • **the override bucket** (§8) — `overrides` are its members, and `worklist`
 *     is the derived worklist with the closed ones removed. `rawWorklist` is what
 *     the classifier produced, kept because the server-side approve gate derives
 *     from that one and the two must stay comparable.
 *   • **the ready mark** — a fully-green issue the reviewer edited inside stays on
 *     the worklist until their explicit Continue. Session-only here (see
 *     `flowSession.ts`), which is why it is layered rather than derived.
 *
 * Keeping both outside `deriveWorklist` is deliberate: that function is pinned
 * byte-for-byte against its Python twin by `threeStateParity.test.ts`, and the
 * override store is client-only on this branch.
 */
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useMemo } from "react"
import {
  useDeclarationArticleStates,
  useDeclarationConditionFields,
  useDeclarationReadModel,
  useDeclarationStatements,
  type DeclarationFrameworkSlice,
} from "../../../../api/declarationsApi"
import type { DeclarationReadModel } from "../../../../api/types"
import {
  deriveCellStates,
  deriveFrameworkEditionStates,
  deriveScopeStates,
  deriveStatementStates,
  type CellState,
  type StateContext,
  type WorklistIssue,
} from "./cellState"
import { useFlowSession, type FlowSession } from "./flowSession"
import {
  buildCollisionItems,
  deriveOpenWork,
  type OverrideMismatch,
} from "./flowState"
import { flowInputsFromSlice } from "./frameworkSliceInputs"
import { type OverrideView } from "./overrides"
import { deriveIssueArticles, type IssueArticles } from "./queueScope"
import { collisionNumbers, statementTitle } from "./statementTitle"

export interface DeclarationFlowState {
  ctx: StateContext | null
  /** The orion read model as fetched, verbatim. Exposed so the screens can read the
   *  two GRAPH-DERIVED fields beside their confirm controls — `recompute_cones` (what
   *  a confirm will re-run) and `field_producers` (which node kind publishes each
   *  field, so a running cone can be mapped onto the units it locks) — without a
   *  second fetch and without holding an edge list of their own. */
  readModel: DeclarationReadModel | null
  /** Cell states keyed `framework:articleNumber` (null cells omitted). */
  cellStates: Record<string, CellState>
  /** Scope states keyed `framework:groupIndex`. */
  scopeStates: Record<string, CellState>
  /** Step-1 identity states keyed `framework:groupIndex`. */
  statementStates: Record<string, CellState>
  /** The reference-list edition axis, keyed by framework slug. Only the frameworks
   *  with a LIVE disagreement appear — there is no settled reading, so a framework
   *  that agrees is absent rather than green. */
  frameworkStates: Record<string, CellState>
  /** The worklist the reviewer works: the derivation minus the work an override
   *  closed. This is what the rail walks and what the buckets count. */
  worklist: WorklistIssue[]
  /** The classifier's own output, before the override layer — the same bytes the
   *  server-side approve gate derives from. */
  rawWorklist: WorklistIssue[]
  /** The Override bucket's members (§8). */
  overrides: OverrideView[]
  /** Notes whose PAIRED FACT does not hold, so the disposition is not in force and
   *  the unit's work is still open. Deliberately not members of `overrides` — that
   *  list is what IS in force — so a surface can say the write only half landed
   *  instead of showing the reader nothing at all. */
  overrideMismatches: OverrideMismatch[]
  /** Issue key → the articles that issue touches (the article scope's input). */
  issueArticles: IssueArticles
  /** Collision number per statement id, for stable `type · verdict · N` titles. */
  collisions: Map<string, number | null>
  /** The statement's app-wide title, by id. */
  titleOf: (statementId: string) => string
  /** The session's own marks — ready issues and their item ledgers. */
  session: FlowSession
  isLoading: boolean
  isError: boolean
}

const EMPTY_SESSION: FlowSession = { pendingIssues: {} }

const EMPTY: DeclarationFlowState = {
  ctx: null,
  readModel: null,
  cellStates: {},
  scopeStates: {},
  statementStates: {},
  frameworkStates: {},
  worklist: [],
  rawWorklist: [],
  overrides: [],
  overrideMismatches: [],
  issueArticles: {},
  collisions: new Map(),
  titleOf: () => "",
  session: EMPTY_SESSION,
  isLoading: false,
  isError: false,
}

/**
 * STEP C3-b2: the paged review's source — ONE regulation's slice instead of the four
 * full reads. The derivation itself is the same `deriveOpenWork`, fed the slice
 * reshaped into today's inputs (`flowInputsFromSlice`). Title numbers come from the
 * server's run-wide numbering (`/open-items` `frameworks`), because numbering one
 * regulation's statements alone would restart at the first collision.
 */
export interface FlowSliceSource {
  slice: DeclarationFrameworkSlice | undefined
  isLoading: boolean
  isError: boolean
  collisions: Map<string, number | null>
}

export function useDeclarationFlowState(
  declId: string,
  source?: FlowSliceSource,
): DeclarationFlowState {
  const { translate } = useTranslation()
  // Paged: the four full reads stay off the wire (`source` is fixed per page mount).
  const full = { enabled: source === undefined }
  const statementsQ = useDeclarationStatements(declId, full)
  const readModelQ = useDeclarationReadModel(declId, full)
  const articleStatesQ = useDeclarationArticleStates(declId, full)
  const conditionFieldsQ = useDeclarationConditionFields(declId, full)
  const session = useFlowSession(declId)

  const isLoading = source
    ? source.isLoading
    : statementsQ.isLoading ||
      readModelQ.isLoading ||
      articleStatesQ.isLoading ||
      conditionFieldsQ.isLoading
  const isError = source
    ? source.isError
    : statementsQ.isError ||
      readModelQ.isError ||
      articleStatesQ.isError ||
      conditionFieldsQ.isError

  // Depend on the stable React Query data refs; the `?? []` fallbacks live inside
  // the memo so an undefined-during-load value doesn't churn the dependency array.
  const statementsData = statementsQ.data
  const readModel = readModelQ.data ?? null
  const articleStatesData = articleStatesQ.data
  const conditionFieldsData = conditionFieldsQ.data
  const slice = source?.slice
  const sliceCollisions = source?.collisions

  return useMemo(() => {
    const inputs = source
      ? slice
        ? flowInputsFromSlice(slice)
        : null
      : readModel
        ? {
            statements: statementsData ?? [],
            readModel,
            articleStates: articleStatesData?.articles ?? [],
            conditionRows: conditionFieldsData?.rows ?? [],
          }
        : null
    if (!inputs) return { ...EMPTY, session, isLoading, isError }
    const statements = inputs.statements
    const collisions =
      sliceCollisions ?? collisionNumbers(buildCollisionItems(statements))
    const titleFor = (ctx: StateContext) => {
      const factOf = new Map(ctx.statements.map((s) => [s.id, s]))
      return (statementId: string): string => {
        const s = factOf.get(statementId)
        return statementTitle(
          s?.framework ?? "",
          s?.verdict ?? null,
          collisions.get(statementId) ?? null,
          translate,
        )
      }
    }
    // The pipeline itself lives in `deriveOpenWork`, pinned to the server's twin by
    // the shared flowParity fixture: the notes IN FORCE come straight off the read
    // model (the persisted store rather than a session copy of it), and the
    // catalogue frameworks gate step 1's unknown-edition needs-verification.
    const { ctx, overrides, rawWorklist, worklist, overrideMismatches } =
      deriveOpenWork({
        ...inputs,
        pendingIssues: session.pendingIssues,
        label: titleFor,
      })
    const titleOf = titleFor(ctx)
    return {
      ctx,
      readModel: inputs.readModel,
      cellStates: deriveCellStates(ctx),
      scopeStates: deriveScopeStates(ctx),
      statementStates: deriveStatementStates(ctx),
      frameworkStates: deriveFrameworkEditionStates(ctx),
      worklist,
      rawWorklist,
      overrides,
      overrideMismatches,
      issueArticles: deriveIssueArticles(ctx, worklist),
      collisions,
      titleOf,
      session,
      isLoading,
      isError,
    }
    // `source` itself is a fresh object per render; its parts are the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statementsData,
    readModel,
    articleStatesData,
    conditionFieldsData,
    slice,
    sliceCollisions,
    session,
    isLoading,
    isError,
    translate,
  ])
}
